import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { capitalize } from "lodash";
import { useFragment } from "react-relay";

import {
  ClockIcon,
  CurrencyIcon,
  PeopleFillIcon,
  RecurringIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  PunchPassAppliedIcon,
  PunchPassAvailableIcon,
} from "@whoosh/design-system/src/components/icons_v2/pos";
import {
  Text,
  Box,
  HStack,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { durationDisplayFromMinutesLong } from "../../../../../../../../../utils/formatTime";
import { getPunchPassSummaryAtom } from "../../../../atoms/selectors/getPunchPassSummaryAtom/getPunchPassSummaryAtom";
import { PunchPassSummary } from "../../../../atoms/types";
import { useGetBookingFees } from "../../hooks/useGetBookingFees";
import { RoundListAdditionalBookingInfoItemDisplay_Booking$key } from "./__generated__/RoundListAdditionalBookingInfoItemDisplay_Booking.graphql";
import { AdditionalBookingInfoItemType } from "./types";

type Props = {
  bookingRef: RoundListAdditionalBookingInfoItemDisplay_Booking$key | null;
  item: AdditionalBookingInfoItemType;
  onToggleViewMore?: () => void;
};

export const RoundListAdditionalBookingInfoItemDisplay = ({
  bookingRef,
  item,
  onToggleViewMore,
}: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const booking = useFragment(
    RoundListAdditionalBookingInfoItemDisplayFragment,
    bookingRef
  );
  const punchPassSummary: PunchPassSummary | null = useAtomValue(
    getPunchPassSummaryAtom(booking?.id ?? null)
  );

  const { bookingFeesTotal } = useGetBookingFees({
    bookingFragmentRef: booking ?? null,
  });

  const courseNoun = getNounOverride(
    booking?.startSlot?.agenda?.facility?.copyOverrides?.course?.singular,
    t`Venue`
  );
  const venueLabel = capitalize(courseNoun);
  const capacity = booking?.startSlot?.standardCapacity || 0;
  const shouldShowRecurringBooking =
    !!booking?.seriesIndex && (booking?.seriesCount ?? 0) > 1;

  const hasPunchPassApplied = punchPassSummary?.hasPunchPassApplied ?? false;
  const hasValidPunchPass = punchPassSummary?.hasValidPunchPass ?? false;

  if (
    (item.type === "BOOKING_FEE" && bookingFeesTotal === 0) ||
    (item.type === "BOOKING_DURATION" &&
      (!booking?.durationMins || booking?.durationMins === 0)) ||
    (item.type === "RECURRING" && !shouldShowRecurringBooking) ||
    (item.type === "VENUE" && booking?.startSlot?.__typename === "QueueSlot")
  ) {
    return null;
  }
  const shouldShowBookingFees = bookingFeesTotal && bookingFeesTotal > 0;

  const rightValue = (() => {
    switch (item.type) {
      case "BOOKING_DURATION":
        if (booking?.durationMins && booking?.durationMins > 0) {
          return durationDisplayFromMinutesLong(booking?.durationMins);
        }
        return null;
      case "BOOKING_FEE":
        return shouldShowBookingFees
          ? `${currencySymbol}${ConvertCentsToDollars(bookingFeesTotal)}`
          : null;
      case "BOOKING_CHARGE":
        return `${currencySymbol}${ConvertCentsToDollars(item.total)}`;
      case "VENUE":
        return booking?.startSlot?.course?.name ?? "-";
      case "RECURRING":
        return t`${booking?.seriesIndex ?? 0} of ${booking?.seriesCount ?? 0}`;
      case "CAPACITY":
        if (!capacity) {
          return "-";
        }
        return capacity === 1 ? t`1 Person` : t`1-${capacity} People`;
      default:
        return null;
    }
  })();

  const content = (
    <HStack
      justifyContent="space-between"
      alignItems="center"
      paddingVertical={2}
    >
      <HStack gap={3} alignItems="center">
        {item.type !== null ? (
          <Box
            width={24}
            height={24}
            borderRadius="2"
            backgroundColor="surface-main-primary-subtlest"
            justifyContent="center"
            alignItems="center"
          >
            {item.type === "BOOKING_DURATION" ? <ClockIcon /> : null}
            {item.type === "BOOKING_FEE" && shouldShowBookingFees ? (
              <CurrencyIcon />
            ) : null}
            {item.type === "RECURRING" ? <RecurringIcon /> : null}
            {item.type === "BOOKING_CHARGE" || item.type === "VENUE" ? (
              <CurrencyIcon />
            ) : null}
            {item.type === "CAPACITY" ? <PeopleFillIcon /> : null}
          </Box>
        ) : null}
        <Text variant="mainBaseSemiBold" color="text-primary">
          {item.type === "BOOKING_DURATION" ? t`Booking Duration` : null}
          {item.type === "BOOKING_FEE" &&
          bookingFeesTotal &&
          bookingFeesTotal > 0
            ? t`Booking Fee`
            : null}
          {item.type === "BOOKING_CHARGE" ? t`Booking Charge` : null}
          {item.type === "VENUE" ? t`${venueLabel}` : null}
          {item.type === "RECURRING" ? t`Recurring Booking` : null}
          {item.type === "CAPACITY" ? t`${venueLabel} Capacity` : null}
        </Text>
      </HStack>

      {item.type === "BOOKING_CHARGE" ? (
        <HStack alignItems="center" gap={2}>
          {hasPunchPassApplied ? (
            <PunchPassAppliedIcon />
          ) : hasValidPunchPass ? (
            <PunchPassAvailableIcon />
          ) : null}
          <Text variant="mainBaseSemiBold" color="text-primary">
            {rightValue}
          </Text>
        </HStack>
      ) : (
        <Text variant="mainBaseSemiBold" color="text-primary">
          {rightValue}
        </Text>
      )}
    </HStack>
  );

  if (item.type === "BOOKING_CHARGE" && onToggleViewMore) {
    return (
      <Pressable
        onPress={onToggleViewMore}
        testID="booking-charge-summary-pressable"
      >
        {content}
      </Pressable>
    );
  }

  return content;
};

const RoundListAdditionalBookingInfoItemDisplayFragment = graphql`
  fragment RoundListAdditionalBookingInfoItemDisplay_Booking on Booking {
    id
    ...useGetBookingFees_Booking
    startSlot {
      __typename
      ... on TimeSlot {
        standardCapacity: capacity(bookingType: STANDARD)
        course {
          name
        }
      }
      ... on QueueSlot {
        standardCapacity: capacity(bookingType: STANDARD)
      }
      agenda {
        facility {
          copyOverrides {
            course {
              singular
            }
          }
        }
      }
    }
    ...useGetSummaryCalculations_Booking
    ... on StandardBooking {
      durationMins
      seriesIndex
      seriesCount
    }
    ... on LessonBooking {
      seriesIndex
      seriesCount
    }
  }
`;
